import { Compass } from "lucide-react";

export function CompassCenter({ compassDirection, overallEngagement, streak, isCompact }) {
  const size = isCompact ? 56 : 72;
  const innerSize = isCompact ? 40 : 52;
  
  // Calculate needle rotation based on compass direction (4 cardinal directions)
  const getRotation = () => {
    const directions = {
      "self-discovery-evolution": -90,              // Top (North)
      "go-beyond-the-comfort-zone": 180,            // Left (West)
      "feel-comfortable-in-your-uniqueness": 0,     // Right (East)
      "walk-all-the-paths-meant-for-you": 90        // Bottom (South)
    };
    return directions[compassDirection] || 0;
  };

  return (
    <div 
      className="absolute left-1/2 -translate-x-1/2"
      style={{ top: isCompact ? "55%" : "50%", transform: "translate(-50%, -50%)" }}
      data-testid="compass-center"
    >
      {/* Outer brass ring */}
      <div 
        className="rounded-full relative flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: "linear-gradient(145deg, #B8956F 0%, #8B7355 50%, #6B5344 100%)",
          boxShadow: "0 0 30px rgba(184, 149, 111, 0.3), inset 0 1px 1px rgba(255,255,255,0.2)"
        }}
      >
        {/* Cardinal direction marks */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
          {[0, 90, 180, 270].map((angle, i) => (
            <line
              key={angle}
              x1="50"
              y1="8"
              x2="50"
              y2="14"
              stroke="#2a1810"
              strokeWidth="2"
              transform={`rotate(${angle} 50 50)`}
              opacity={i === 0 ? 1 : 0.5}
            />
          ))}
          {/* Smaller marks */}
          {[45, 135, 225, 315].map((angle) => (
            <line
              key={angle}
              x1="50"
              y1="10"
              x2="50"
              y2="14"
              stroke="#2a1810"
              strokeWidth="1"
              transform={`rotate(${angle} 50 50)`}
              opacity="0.3"
            />
          ))}
        </svg>

        {/* Inner compass face */}
        <div 
          className="rounded-full flex items-center justify-center relative overflow-hidden"
          style={{
            width: innerSize,
            height: innerSize,
            background: "linear-gradient(180deg, #1a0f08 0%, #2a1810 50%, #1a0f08 100%)",
            border: "2px solid #6B5344",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.5)"
          }}
        >
          {/* Compass rose lines */}
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100">
            <line x1="50" y1="20" x2="50" y2="80" stroke="#B8956F" strokeWidth="0.5"/>
            <line x1="20" y1="50" x2="80" y2="50" stroke="#B8956F" strokeWidth="0.5"/>
            <line x1="30" y1="30" x2="70" y2="70" stroke="#B8956F" strokeWidth="0.3"/>
            <line x1="70" y1="30" x2="30" y2="70" stroke="#B8956F" strokeWidth="0.3"/>
          </svg>

          {/* Compass needle */}
          <svg 
            className="absolute inset-0 w-full h-full transition-transform duration-1000"
            viewBox="0 0 100 100"
            style={{ transform: `rotate(${getRotation()}deg)` }}
          >
            {/* North needle (red/amber) */}
            <polygon
              points="50,18 46,50 50,45 54,50"
              fill="#C8785D"
              stroke="#8B5040"
              strokeWidth="0.5"
            />
            {/* South needle (dark) */}
            <polygon
              points="50,82 46,50 50,55 54,50"
              fill="#3a2820"
              stroke="#2a1810"
              strokeWidth="0.5"
            />
            {/* Center pivot */}
            <circle cx="50" cy="50" r="4" fill="#B8956F" stroke="#6B5344" strokeWidth="1"/>
          </svg>

          {/* User position indicator */}
          <div 
            className="absolute w-2 h-2 rounded-full"
            style={{
              background: "#D4A574",
              boxShadow: "0 0 8px #D4A574"
            }}
          />
        </div>

        {/* Streak indicator (small badge) */}
        {streak > 0 && !isCompact && (
          <div 
            className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold"
            style={{
              background: "linear-gradient(145deg, #C8785D, #8B5040)",
              color: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
            }}
          >
            {streak}🔥
          </div>
        )}
      </div>

      {/* "You are here" label */}
      {!isCompact && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2">
          <p className="text-[#B8956F]/70 text-[9px] tracking-widest uppercase whitespace-nowrap">
            You Are Here
          </p>
        </div>
      )}
    </div>
  );
}

export default CompassCenter;
